import request from '../utils/request'

/**
 * 获取水位计设备实时数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} ip - 指定IP地址(可选)
 * @param {number} port - 指定端口号(可选)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     dataTimestamp: string,
 *     deviceId: number,
 *     deviceName: string,
 *     deviceSubType: string,
 *     deviceType: string,
 *     extraProperties: object,
 *     ip: string,
 *     port: number,
 *     realTimeValue: object,
 *     unit: string
 *   }>
 * }>} 水位计实时数据列表
 */
export const getWaterLevelRealTimeData = (bridgeId, ip, port) => {
  const params = { bridgeId }
  if (ip) params.ip = ip
  if (port) params.port = port
  
  return request({
    url: '/device-realtime/waterlevel',
    method: 'get',
    params
  })
}

/**
 * 获取激光设备实时数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} ip - 指定IP地址(可选)
 * @param {number} port - 指定端口号(可选)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     dataTimestamp: string,
 *     deviceId: number,
 *     deviceName: string,
 *     deviceSubType: string,
 *     deviceType: string,
 *     extraProperties: object,
 *     ip: string,
 *     port: number,
 *     realTimeValue: object,
 *     unit: string
 *   }>
 * }>} 激光设备实时数据列表
 */
export const getLaserRealTimeData = (bridgeId, ip, port) => {
  const params = { bridgeId }
  if (ip) params.ip = ip
  if (port) params.port = port
  
  return request({
    url: '/device-realtime/laser',
    method: 'get',
    params
  })
}

/**
 * 获取设备实时数据
 * @param {object} queryDTO - 查询条件
 * @param {number} queryDTO.bridgeId - 桥梁ID
 * @param {string} queryDTO.deviceType - 设备类型
 * @param {string} queryDTO.ip - IP地址(可选)
 * @param {number} queryDTO.port - 端口号(可选)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     dataTimestamp: string,
 *     deviceId: number,
 *     deviceName: string,
 *     deviceSubType: string,
 *     deviceType: string,
 *     extraProperties: object,
 *     ip: string,
 *     port: number,
 *     realTimeValue: object,
 *     unit: string
 *   }>
 * }>} 设备实时数据列表
 */
export const getDeviceRealTimeData = (queryDTO) => {
  return request({
    url: '/device-realtime/data',
    method: 'post',
    data: queryDTO
  })
}

/**
 * 获取支持的设备类型列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: string[]
 * }>} 设备类型列表
 */
export const getSupportedDeviceTypes = () => {
  return request({
    url: '/device-realtime/supported-types',
    method: 'get'
  })
}

/**
 * 验证设备类型是否支持
 * @param {string} deviceType - 设备类型
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: boolean
 * }>} 验证结果
 */
export const validateDeviceType = (deviceType) => {
  return request({
    url: '/device-realtime/validate-type',
    method: 'get',
    params: { deviceType }
  })
}

/**
 * 获取能见度计设备实时数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} ip - 指定IP地址(可选)
 * @param {number} port - 指定端口号(可选)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     dataTimestamp: string,
 *     deviceId: number,
 *     deviceName: string,
 *     deviceSubType: string,
 *     deviceType: string,
 *     extraProperties: object,
 *     ip: string,
 *     port: number,
 *     realTimeValue: object,
 *     unit: string
 *   }>
 * }>} 能见度计实时数据列表
 */
export const getVisibilityRealTimeData = (bridgeId, ip, port) => {
  const data = { 
    bridgeId
  }
  if (ip) data.ip = ip
  if (port) data.port = port
  
  return request({
    url: '/device-realtime/visibility',
    method: 'get',
    params: data
  })
}

/**
 * 获取风速风向仪设备实时数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} ip - 指定IP地址(可选)
 * @param {number} port - 指定端口号(可选)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     dataTimestamp: string,
 *     deviceId: number,
 *     deviceName: string,
 *     deviceSubType: string,
 *     deviceType: string,
 *     extraProperties: object,
 *     ip: string,
 *     port: number,
 *     realTimeValue: object,
 *     unit: string
 *   }>
 * }>} 风速风向仪实时数据列表
 */
export const getAnemometerRealTimeData = (bridgeId, ip, port) => {
  const params = { bridgeId }
  if (ip) params.ip = ip
  if (port) params.port = port

  return request({
    url: '/device-realtime/anemometer',
    method: 'get',
    params
  })
}

/**
 * 获取流速计设备实时数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} ip - 指定IP地址(可选)
 * @param {number} port - 指定端口号(可选)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     dataTimestamp: string,
 *     deviceId: number,
 *     deviceName: string,
 *     deviceSubType: string,
 *     deviceType: string,
 *     extraProperties: object,
 *     ip: string,
 *     port: number,
 *     realTimeValue: object,
 *     unit: string
 *   }>
 * }>} 流速计实时数据列表
 */
export const getFlowmeterRealTimeData = (bridgeId, ip, port) => {
  const params = { bridgeId }
  if (ip) params.ip = ip
  if (port) params.port = port

  return request({
    url: '/device-realtime/flowmeter',
    method: 'get',
    params
  })
}
